import { useEffect, useState } from "react";
import { Navigate, useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Package, Trash2, TrendingUp, Truck, StickyNote } from "lucide-react";

export default function EncomendaDetail() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!user || !id) return;
    (async () => {
      try {
        const { data } = await api.get(`/encomendas/${id}`);
        setData(data);
      } catch { toast.error("Erro ao carregar encomenda"); }
      finally { setLoading(false); }
    })();
  }, [user, id]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const handleDelete = async () => {
    try {
      await api.delete(`/encomendas/${id}`);
      toast.success("Encomenda eliminada");
      navigate("/dashboard/encomendas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="encomenda-detail-page">
      <DashboardHeader title="Detalhe da encomenda" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {loading || !data ? (
          <div className="h-24 bg-white rounded-2xl lb-shadow-card animate-pulse" />
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 lb-fade-in">
              <div>
                <div className="lb-overline">Encomenda de {new Date(data.received_at || data.created_at).toLocaleDateString("pt-PT")}</div>
                <h1 className="font-display text-3xl sm:text-4xl mt-1">{data.supplier || "Sem fornecedor"}</h1>
                {data.notes && <p className="text-sm text-muted-foreground mt-2 max-w-2xl"><StickyNote className="w-4 h-4 inline mr-1" />{data.notes}</p>}
              </div>
              <Button variant="outline" onClick={() => setConfirmDelete(true)} className="text-[hsl(var(--destructive))]" data-testid="delete-encomenda-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Total investido" value={format(data.summary.total_invested)} sub={`inclui envio ${format(data.shipping_cost || 0)}`} />
              <Kpi label="Lucro realizado" value={format(data.summary.profit_realized)} sub={`${data.summary.sold_qty} un. vendidas`} accent />
              <Kpi label="Lucro potencial" value={format(data.summary.potential_profit)} sub={`${data.summary.remaining_qty} un. restantes`} />
              <Kpi label="Sell-through" value={`${data.summary.sell_through_pct}%`} sub={`${data.summary.sold_qty} / ${data.summary.total_qty}`} />
            </div>

            {/* Items */}
            <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5">
              <h2 className="font-display text-xl mb-3">Itens da encomenda</h2>
              <div className="space-y-2">
                {data.items.map((it) => {
                  const sold = it.quantity - it.quantity_remaining;
                  return (
                    <div key={it.id} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-secondary rounded-xl p-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{it.product_name} <span className="text-muted-foreground font-normal">· {it.variant_name}</span></div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {sold} vendida(s) de {it.quantity} · Custo unit. {format(it.unit_purchase_price)}
                          {it.suggested_sale_price > 0 && ` · Venda sugerida ${format(it.suggested_sale_price)}`}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm">Total: {format(it.quantity * it.unit_purchase_price)}</div>
                        <div className="text-xs text-muted-foreground">Restam {it.quantity_remaining}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {(data.shipping_cost > 0 || data.extra_fees > 0) && (
                <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-2 text-sm">
                  <div><Truck className="w-4 h-4 inline mr-1 text-muted-foreground" /> Envio: {format(data.shipping_cost || 0)}</div>
                  <div>Outras taxas: {format(data.extra_fees || 0)}</div>
                </div>
              )}
            </section>

            {/* Related sales */}
            <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5">
              <h2 className="font-display text-xl mb-3">Vendas desta encomenda</h2>
              {data.related_sales.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ainda sem vendas associadas.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.related_sales.map((s) => (
                    <div key={s.id + s.allocated_qty} className="text-sm flex items-center justify-between border-b border-border py-2 last:border-0">
                      <div className="min-w-0">
                        <div className="truncate">{s.product_name} · {s.variant_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(s.created_at).toLocaleDateString("pt-PT")} · {s.payment_type}
                          {s.customer_name ? ` · ${s.customer_name}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <div className="text-sm">{s.allocated_qty}× {format(s.sale_price)}</div>
                        <div className="text-xs font-semibold text-[hsl(var(--primary))]">
                          Lucro: {format((s.sale_price - s.unit_purchase_price) * s.allocated_qty)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar encomenda?</AlertDialogTitle>
            <AlertDialogDescription>Só é possível eliminar encomendas sem vendas associadas. O stock por vender será removido.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[hsl(var(--destructive))] hover:bg-[#B03838]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl p-4 bg-white lb-shadow-card">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-display text-xl ${accent ? "text-[hsl(var(--primary))]" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
